import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PersonIcon from "@mui/icons-material/Person";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { Chip, CircularProgress, Paper, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";

import { useWorker } from "../api/useWorker";
import { type Visit, VISIT_TYPE_LABELS, VisitStatus, VisitType } from "../types";

function statusLabel(status: VisitStatus): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function VisitStatusChip({ status }: { status: VisitStatus }) {
  if (status === VisitStatus.OPEN) {
    return <Chip label={statusLabel(status)} variant="outlined" size="small" />;
  }

  return (
    <Chip
      label={statusLabel(status)}
      size="small"
      sx={{
        bgcolor: (theme) => theme.palette.text.primary,
        color: (theme) => theme.palette.background.paper,
      }}
    />
  );
}

function BookedWorkerName({ workerId }: { workerId: string }) {
  const { data: worker, isLoading } = useWorker(workerId, { useErrorBoundary: false });

  if (isLoading) {
    return <CircularProgress size={14} />;
  }

  return (
    <Text variant="body1" data-dd-privacy="mask">
      {worker?.name}
    </Text>
  );
}

interface Props {
  visit: Visit;
}

export function MobileVisitCard({ visit }: Props) {
  const { attributes } = visit;
  const {
    type,
    status,
    workerReq,
    deadline,
    visitsPerWeek,
    durationInWeeks,
    payRate,
    chargeRate,
    bookedWorkerId,
  } = attributes;

  const visitTitle = [VISIT_TYPE_LABELS[type], workerReq].filter(Boolean).join(" ");

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        px: 2.5,
        py: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
        padding: "1rem",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="h6">{visitTitle}</Text>
          <VisitStatusChip status={status} />
        </Stack>

        {isDefined(bookedWorkerId) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
            <BookedWorkerName workerId={bookedWorkerId} />
          </Stack>
        )}

        {isDefined(deadline) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">
              First visit due: {formatShortMonthDayYear(new Date(deadline))}
            </Text>
          </Stack>
        )}

        {type === VisitType.REGULAR && isDefined(visitsPerWeek) && isDefined(durationInWeeks) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <RadioButtonUncheckedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">
              {visitsPerWeek}/week for {durationInWeeks} weeks
            </Text>
          </Stack>
        )}

        {isDefined(payRate) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MonetizationOnOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">Pay rate: ${(payRate.amountInMinorUnits / 100).toFixed(2)}</Text>
          </Stack>
        )}

        {isDefined(chargeRate) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
            <Text variant="body1">
              Charge rate: ${(chargeRate.amountInMinorUnits / 100).toFixed(2)}
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
